import { useCallback } from "react";

import { useFireAnalyticsEvents } from "@/hooks/use-fire-analytics-events";

export const useGrowthSliderTracking = () => {
    const { fireUIEvent } = useFireAnalyticsEvents();

    const fireSliderClickedEvent = useCallback(
        (value: number) => {
            fireUIEvent({
                action: "clicked",
                actionSubject: "slider",
                actionSubjectId: "growthSlider",
                attributes: {
                    value
                }
            });
        },
        [fireUIEvent]
    );

    return { fireSliderClickedEvent };
};

export const useAiChatAgentsTracking = () => {
    const { fireUIEvent } = useFireAnalyticsEvents();

    const fireChatAdoptionChangedEvent = useCallback(
        (value: number) => {
            fireUIEvent({
                action: "changed",
                actionSubject: "dropdown",
                actionSubjectId: "chatAdoptionPercent",
                attributes: {
                    value
                }
            });
        },
        [fireUIEvent]
    );

    const fireChatQueriesPerUserChangedEvent = useCallback(
        (value: number) => {
            fireUIEvent({
                action: "changed",
                actionSubject: "dropdown",
                actionSubjectId: "chatUsersPerRulePerMonth",
                attributes: {
                    value
                }
            });
        },
        [fireUIEvent]
    );

    return { fireChatAdoptionChangedEvent, fireChatQueriesPerUserChangedEvent };
};

export const useAiAutomationTracking = () => {
    const { fireUIEvent } = useFireAnalyticsEvents();

    const fireAutomationActiveRulesChangedEvent = useCallback(
        (value: number) => {
            fireUIEvent({
                action: "changed",
                actionSubject: "dropdown",
                actionSubjectId: "automationActiveRules",
                attributes: {
                    value
                }
            });
        },
        [fireUIEvent]
    );

    const fireAutomationRunsPerRuleChangedEvent = useCallback(
        (value: number) => {
            fireUIEvent({
                action: "changed",
                actionSubject: "dropdown",
                actionSubjectId: "automationRunsPerRulePerMonth",
                attributes: {
                    value
                }
            });
        },
        [fireUIEvent]
    );

    return {
        fireAutomationActiveRulesChangedEvent,
        fireAutomationRunsPerRuleChangedEvent
    };
};

export const useSolutionPlansTracking = () => {
    const { fireUIEvent } = useFireAnalyticsEvents();

    const firePlanChangedEvent = useCallback(
        (collectionKey: string, plan: string) => {
            fireUIEvent({
                action: "changed",
                actionSubject: "dropdown",
                actionSubjectId: "solutionPlan",
                attributes: {
                    collectionKey,
                    plan
                }
            });
        },
        [fireUIEvent]
    );

    return { firePlanChangedEvent };
};

export const useIndustrySelectorTracking = () => {
    const { fireUIEvent } = useFireAnalyticsEvents();

    const fireCollectionToggledEvent = useCallback(
        (collectionKey: string, selected: boolean) => {
            fireUIEvent({
                action: "clicked",
                actionSubject: "button",
                actionSubjectId: "industryCollectionPill",
                attributes: {
                    collectionKey,
                    selected
                }
            });
        },
        [fireUIEvent]
    );

    return { fireCollectionToggledEvent };
};

export const useIndexedObjectsTracking = () => {
    const { fireUIEvent } = useFireAnalyticsEvents();

    const fireIndexed3rdPartyToolsChangedEvent = useCallback(
        (value: number) => {
            fireUIEvent({
                action: "changed",
                actionSubject: "dropdown",
                actionSubjectId: "indexedObjects3rdPartyTools",
                attributes: {
                    value
                }
            });
        },
        [fireUIEvent]
    );

    const fireIndexedObjectsPerToolChangedEvent = useCallback(
        (value: number) => {
            fireUIEvent({
                action: "changed",
                actionSubject: "dropdown",
                actionSubjectId: "indexedObjectsPerTool",
                attributes: {
                    value
                }
            });
        },
        [fireUIEvent]
    );

    return {
        fireIndexed3rdPartyToolsChangedEvent,
        fireIndexedObjectsPerToolChangedEvent
    };
};
